import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Stack, styled, Typography } from '@mui/material';
import { IconDocument, IconDownload } from 'assets/img';
import { FormProvider, useFormContext } from 'react-hook-form';
import { DynamicForm } from 'redesign';
import { SignDocumentDialogBodyProps } from './sign-document-dialog-body.types';
import { getPayrollTaxStatusIcon } from '../../../../../table/utils/get-documents-payroll-tax-status';
import { getDocumentStatusIcon } from '../../../../../table/utils/get-document-status-icon';
import { useDisplayDocument } from '../../../../../table/hooks/use-display-document';

export const SignDocumentDialogBody = ({
  payrollTaxEnabled,
  status,
  selectedDocumentModel,
}: SignDocumentDialogBodyProps) => {
  const { t } = useTranslation(['web-common', 'hq-screen-specific']);
  const methods = useFormContext();
  const { handleDownload, isDownloading } = useDisplayDocument(methods.getValues('documentId'));

  const key = `documents.status.${status.toLowerCase()}`;

  return (
    <FormProvider {...methods}>
      <FlexColumnBoxWrap>
        <DocumentInfo>
          <DocumentWrap>
            <FlexRowBoxCenter onClick={handleDownload} role="download-document">
              <StyledIconDocument width={120} height={150} />
              {isDownloading ? (
                <SpinnerOverlay size={24} data-testid="download-spinner" />
              ) : (
                <StyledIconDownload />
              )}
            </FlexRowBoxCenter>
          </DocumentWrap>
          <DocumentInfoWrapper>
            <Stack direction="row" spacing={5} alignItems="center" paddingTop="8px">
              <Box display="flex" alignItems="center" gap={1}>
                <StyledIcon as={getDocumentStatusIcon(status)} />
                <Typography noWrap>{t(key, { ns: 'web-common' })}</Typography>
              </Box>
              {payrollTaxEnabled !== undefined ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <StyledIcon as={getPayrollTaxStatusIcon(payrollTaxEnabled)} />
                  <Typography noWrap>
                    {payrollTaxEnabled
                      ? t('documents.payrollTax.applied', { ns: 'hq-screen-specific' })
                      : t('documents.payrollTax.not_applied', { ns: 'hq-screen-specific' })}
                  </Typography>
                </Box>
              ) : null}
            </Stack>
            <FormBox>
              <DynamicForm config={selectedDocumentModel} />
            </FormBox>
          </DocumentInfoWrapper>
        </DocumentInfo>
      </FlexColumnBoxWrap>
    </FormProvider>
  );
};

const FlexColumnBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const FlexRowBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
}));

const FlexColumnBoxWrap = styled(FlexColumnBox)(() => ({
  overflow: 'auto',
  margin: '0 20px 0 20px',
}));

const DocumentInfo = styled(FlexRowBox)(({ theme }) => ({
  marginTop: theme.spacing(4),
  gap: theme.spacing(22),
}));

const DocumentWrap = styled(FlexColumnBox)(() => ({
  alignItems: 'flex-start',
  justifyContent: 'center',
}));

const StyledIconDownload = styled(IconDownload)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(6),
  right: theme.spacing(4),
}));

const SpinnerOverlay = styled(CircularProgress)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(6),
  right: theme.spacing(4),
}));

const StyledIconDocument = styled(IconDocument)(() => ({
  zIndex: 1,
  '& path': {
    fill: '#002bff8a',
  },
}));

const FlexRowBoxCenter = styled(FlexRowBox)(() => ({
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  position: 'relative',
}));

const DocumentInfoWrapper = styled(FlexColumnBox)(({ theme }) => ({
  gap: theme.spacing(4),
  width: '100%',
  justifyContent: 'space-between',
}));

const FormBox = styled(Box)(() => ({
  display: 'flex',
}));

const StyledIcon = styled('svg')(({ theme }) => ({
  width: theme.spacing(5),
  height: theme.spacing(5),
  marginRight: theme.spacing(1.5),
}));
